import React, {useEffect, useMemo, useRef} from 'react';
import { useHistory } from "react-router-dom";

import MagnetboardElementary from './elementary/MagnetboardElementary';
import MagnetboardMain from './hsms/magnetboard/MagnetboardMain';
import { userHasManualPlacementPermission } from '../js/departmentResourcePermissions';
import { getDiagnosticTimestamp, logMagnetboardDiagnostic, startMagnetboardDiagnosticTimer } from './hsms/magnetboard/utils/diagnostics';

export default function Magnetboard({ match, isAuth, authComplete, schoolType, schoolSubscribed, schoolInfoLoading, schoolInfo, scheduleInfo, setScheduleInfo, scheduleStructureData, setScheduleStructureData, scheduleInfoLoading, scheduleStructureDataLoading, periodsLoading, periods, userInfo, departmentsLoading, departments, setDepartments, teachersLoading, setTeachersLoading, teachers, setTeachers, coursesLoading, setCoursesLoading, courses, setCourses, classroomsLoading, setClassroomsLoading, classrooms, setClassrooms, studentsLoading, setStudentsLoading, students, studentMatchPercentages, setStudents, blocksLoading, blocks, setBlocks, sectionsLoading, sections, setSections, subsections, setSubsections, lunchInfo, labels, setLabels, loadScreen, setLoadScreen, setScreenAllowsUniversalSidebar, handleDuplicateScheduleVersion, handleDeleteScheduleVersion, handleUpdateScheduleVersionName }) {
	const history = useHistory();
	const renderCountRef = useRef(0);
	const renderStartedAt = getDiagnosticTimestamp();
	renderCountRef.current += 1;

	const supervisorMagnetboardOptions = useMemo(() => {
		if(!userInfo || `${userInfo.role_id}` !== '3') return null;

		const endTimer = startMagnetboardDiagnosticTimer('wrapper-supervisor-options', {
			departments: departments.length,
			courses: courses.length,
			teachers: teachers.length,
			classrooms: classrooms.length,
			sections: sections.length,
		});

		const user_id = `${userInfo.user_id || ''}`;
		const supervisor_allow_cross_department_editing = `${userInfo.allow_cross_department_editing}` === '1' || departments.some(department => {
			const supervisors = Array.isArray(department.supervisors) ? department.supervisors : [];
			return supervisors.some(supervisor => `${supervisor.user_id || ''}` === user_id && `${supervisor.allow_cross_department_editing}` === '1');
		});
		const supervisor_department_ids = departments.reduce((results, department) => {
			const supervisors = Array.isArray(department.supervisors) ? department.supervisors : [];
			if(supervisors.some(supervisor => `${supervisor.user_id || ''}` === user_id)) results.push(department.department_id);
			return results;
		}, []);
		const supervisor_can_manually_place = userHasManualPlacementPermission(departments, userInfo);
		const supervisor_department_id_set = new Set(supervisor_department_ids.map(department_id => `${department_id}`));
		const hasSupervisorDepartment = (department_ids = []) => {
			if(!Array.isArray(department_ids)) return false;
			return department_ids.some(department_id => supervisor_department_id_set.has(`${department_id}`));
		};

		const allowed_course_ids = courses
			.filter(course => hasSupervisorDepartment(course.departments))
			.map(course => course.course_id);
		const allowed_course_id_set = new Set(allowed_course_ids.map(course_id => `${course_id}`));
		const sectionIsSupervisorDepartment = (section) => hasSupervisorDepartment(section?.departments);

		const allowed_teacher_ids = teachers
			.filter(teacher => {
				if(hasSupervisorDepartment(teacher.departments)) return true;
				return sections.some(section =>
					sectionIsSupervisorDepartment(section) &&
					allowed_course_id_set.has(`${section.course_id}`) &&
					(`${section.teacher_id}` === `${teacher.teacher_id}` ||
						(Array.isArray(section.secondary_teachers) && section.secondary_teachers.some(teacher_id => `${teacher_id}` === `${teacher.teacher_id}`)))
				);
			})
			.map(teacher => teacher.teacher_id);

		const allowed_classroom_ids = classrooms
			.filter(classroom => {
				if(hasSupervisorDepartment(classroom.departments)) return true;
				return sections.some(section =>
					sectionIsSupervisorDepartment(section) &&
					`${section.classroom_id || ''}` === `${classroom.classroom_id}`
				);
			})
			.map(classroom => classroom.classroom_id);

		const options = {
			show_top_bar: true,
			allow_schedule_name_editing: false,
			allow_filtering: true,
			show_match_percent: false,
			show_conflicts: true,
			show_menu: true,
			hide_duplicate_schedule: true,
			hide_delete_schedule: true,
			hide_export_schedule: true,
			hide_actions: false,
			allowed_action_groups: ['sections'],
			allowed_section_actions: ['lock', 'unlock'],
			hide_reports: false,
			allowed_reports: ['schedule-summary', 'conflicts'],
			allow_new_sections: supervisor_can_manually_place,
			allow_placeholders: false,
			allow_hotlinks: false,
			restrict_to_allowed_ids: true,
			allow_section_dragging: supervisor_can_manually_place,
			allow_section_structure_editing: supervisor_can_manually_place,
			allow_section_roster_editing: supervisor_can_manually_place,
			allow_locked_section_roster_editing: false,
			allow_section_duplicate: supervisor_can_manually_place,
			allow_section_delete: supervisor_can_manually_place,
			allow_inline_quarter_editing: supervisor_can_manually_place,
			allow_period_availability_editing: true,
			allow_cross_department_editing: supervisor_allow_cross_department_editing,
			allowed_department_ids: supervisor_department_ids,
			allowed_teacher_ids,
			allowed_course_ids,
			allowed_classroom_ids,
			view_options: [
				{value:'teacher', display:'Teacher View'},
				{value:'course', display:'Course View'},
				{value:'classroom', display:'Classroom View'}
			]
		};

		endTimer({
			allowed_departments: supervisor_department_ids.length,
			allowed_courses: allowed_course_ids.length,
			allowed_teachers: allowed_teacher_ids.length,
			allowed_classrooms: allowed_classroom_ids.length,
		});

		return options;
	}, [userInfo, departments, teachers, courses, classrooms, sections]);

	const counselorMagnetboardOptions = useMemo(() => {
		if(!userInfo || `${userInfo.role_id}` !== '4') return null;

		return {
			show_top_bar: true,
			allow_schedule_name_editing: false,
			allow_filtering: true,
			show_match_percent: true,
			show_conflicts: false,
			show_menu: true,
			hide_duplicate_schedule: true,
			hide_delete_schedule: true,
			hide_export_schedule: true,
			hide_actions: true,
			hide_reports: false,
			allow_new_sections: false,
			allow_placeholders: false,
			allow_hotlinks: false,
			restrict_to_allowed_ids: false,
			allow_section_dragging: false,
			allow_section_structure_editing: false,
			allow_section_roster_editing: true,
			allow_locked_section_roster_editing: true,
			allow_section_duplicate: false,
			allow_section_delete: false,
			allow_inline_quarter_editing: false,
			allow_period_availability_editing: false,
			show_section_conflict_indicators: false,
			default_view: 'student',
			view_options: [
				{ value: 'student', display: 'Student View' },
				{ value: 'teacher', display: 'Teacher View' },
				{ value: 'course', display: 'Course View' },
				{ value: 'classroom', display: 'Classroom View' },
			],
		};
	}, [userInfo]);

	//////////////////////////////
	///// DO ON STATE CHANGE /////
	//////////////////////////////
	useEffect(() => {
		logMagnetboardDiagnostic('wrapper-commit', {
			render_count: renderCountRef.current,
			duration_ms: Math.round(getDiagnosticTimestamp() - renderStartedAt),
			school_type: match.params.school_type,
			load_screen: loadScreen,
			sections: sections.length,
			students: students.length,
			teachers: teachers.length,
			courses: courses.length,
			classrooms: classrooms.length,
		});
	});

	useEffect(() => {
		// Check if user is logged in
		// If not, send them to log in screen with a redirect to this screen
		// If yes, check if user has permission
		// If not permission, redirect to loading page
		if(authComplete && !isAuth)
		{
			const redirect = (schoolType) ? `/app/${schoolType}/magnetboard` : `/app/select_school`;
			history.push({
				pathname: '/app/login',
				state: {redirect: redirect}
			});
		}
		else if(authComplete && loadScreen === 'select_school')
		{
			history.push({
				pathname: '/app/select_school',
				state: {redirect: `/app/${schoolType}/magnetboard`}
			});
		}
		else if(authComplete && !schoolInfoLoading)
		{
			setLoadScreen('magnetboard');
		}
	}, [isAuth, authComplete, schoolInfoLoading, loadScreen, history, schoolInfo, schoolType, setLoadScreen]);

	return (
		<>
			{(() => {
				switch(match.params.school_type) {
					case 'hsms':
						return <div className='main-mb-content'>
									<MagnetboardMain isAuth={isAuth} authComplete={authComplete} schoolSubscribed={schoolSubscribed} schoolInfo={schoolInfo} schoolInfoLoading={schoolInfoLoading} scheduleInfo={scheduleInfo} setScheduleInfo={setScheduleInfo} scheduleStructureData={scheduleStructureData} setScheduleStructureData={setScheduleStructureData} scheduleInfoLoading={scheduleInfoLoading} scheduleStructureDataLoading={scheduleStructureDataLoading}  userInfo={userInfo} departmentsLoading={departmentsLoading} departments={departments} setDepartments={setDepartments} teachersLoading={teachersLoading} setTeachersLoading={setTeachersLoading} teachers={teachers} setTeachers={setTeachers} coursesLoading={coursesLoading} setCoursesLoading={setCoursesLoading} courses={courses} setCourses={setCourses} classroomsLoading={classroomsLoading} setClassroomsLoading={setClassroomsLoading} classrooms={classrooms} setClassrooms={setClassrooms} studentsLoading={studentsLoading} setStudentsLoading={setStudentsLoading} students={students} setStudents={setStudents} studentMatchPercentages={studentMatchPercentages} sectionsLoading={sectionsLoading} sections={sections} setSections={setSections} subsections={subsections} setSubsections={setSubsections} lunchInfo={lunchInfo} labels={labels} setLabels={setLabels} setScreenAllowsUniversalSidebar={setScreenAllowsUniversalSidebar} handleDuplicateScheduleVersion={handleDuplicateScheduleVersion} handleDeleteScheduleVersion={handleDeleteScheduleVersion} handleUpdateScheduleVersionName={handleUpdateScheduleVersionName} magnetboardOptions={counselorMagnetboardOptions || supervisorMagnetboardOptions || undefined}/>
							</div>;
					case 'elementary':
						return <MagnetboardElementary schoolSubscribed={schoolSubscribed} schoolInfo={schoolInfo} schoolInfoLoading={schoolInfoLoading} scheduleInfo={scheduleInfo} scheduleInfoLoading={scheduleInfoLoading} periodsLoading={periodsLoading} periods={periods} teachersLoading={teachersLoading} teachers={teachers} setTeachers={setTeachers} coursesLoading={coursesLoading} courses={courses} setCourses={setCourses} classroomsLoading={classroomsLoading} classrooms={classrooms} setClassrooms={setClassrooms} studentsLoading={studentsLoading} students={students} setStudents={setStudents} blocksLoading={blocksLoading} blocks={blocks} setBlocks={setBlocks} />;
					default:
						return null;
				}
			})()}
		</>
	);
}
